// src/pages/CategoryPage.jsx
// Shows subcategories for a category + all active vendors in that category
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  getCategoryById,
  getSubcategoriesByCategoryId,
  getVendorsByCategoryId,
} from '../firebase/services';

export default function CategoryPage() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSubcat, setActiveSubcat] = useState('all');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      getCategoryById(categoryId),
      getSubcategoriesByCategoryId(categoryId),
      getVendorsByCategoryId(categoryId),
    ]).then(([cat, subs, vends]) => {
      if (!cat) { navigate('/'); return; }
      setCategory(cat);
      setSubcategories(subs);
      setVendors(vends);
    }).catch(() => navigate('/')).finally(() => setLoading(false));
  }, [categoryId, navigate]);

  // Filter vendors by selected subcategory
  const filteredVendors = activeSubcat === 'all'
    ? vendors
    : vendors.filter(v =>
        Array.isArray(v.subCategoryIds) &&
        v.subCategoryIds.includes(activeSubcat)
      );

  const isOpen = (openTime) => {
    if (!openTime) return true;
    const now = new Date();
    const day = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'][now.getDay()];
    const todayHours = openTime[day];
    if (!todayHours) return false;
    const [oh, om] = todayHours.open.split(':').map(Number);
    const [ch, cm] = todayHours.close.split(':').map(Number);
    const nowMins = now.getHours() * 60 + now.getMinutes();
    return nowMins >= oh * 60 + om && nowMins <= ch * 60 + cm;
  };

  if (loading) return (
    <div style={{ paddingTop: 68 }}>
      <div style={{ height: 260, background: 'var(--surface)', animation: 'shimmer 1.4s ease infinite', marginBottom: 40 }} />
      <div className="container" style={{ padding: '0 40px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 16, marginBottom: 40 }}>
          {[...Array(6)].map((_, i) => <div key={i} className="skeleton" style={{ height: 80, borderRadius: 8 }} />)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 20 }}>
          {[...Array(4)].map((_, i) => <div key={i} className="skeleton" style={{ height: 200, borderRadius: 10 }} />)}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ paddingTop: 68 }}>
      {/* ── Category Hero ── */}
      <div style={{ position: 'relative', minHeight: 260, display: 'flex', alignItems: 'flex-end' }}>
        <div style={{
          position: 'absolute', inset: 0,
          background: category?.imageUrl
            ? `url('${category.imageUrl}') center/cover`
            : 'var(--surface)',
        }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top,rgba(9,12,10,0.98) 0%,rgba(9,12,10,0.5) 60%,rgba(9,12,10,0.2) 100%)' }} />
        <div className="container" style={{ position: 'relative', zIndex: 1, padding: '40px 40px' }}>
          {/* Breadcrumb */}
          <div style={{ display: 'flex', gap: 8, fontSize: 11, color: 'var(--text-3)', marginBottom: 16 }}>
            <Link to="/" style={{ color: 'var(--text-3)', transition: 'color 0.2s' }} onMouseEnter={e => e.target.style.color = 'var(--gold)'} onMouseLeave={e => e.target.style.color = 'var(--text-3)'}>Home</Link>
            <span>/</span>
            <span style={{ color: 'var(--gold)' }}>{category?.name}</span>
          </div>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(2rem,5vw,3.5rem)', fontWeight: 400 }}>
            {category?.name}
          </h1>
          <p style={{ color: 'var(--text-3)', fontSize: 13, marginTop: 8 }}>
            {vendors.length} vendor{vendors.length !== 1 ? 's' : ''} available
          </p>
        </div>
      </div>

      <div className="container" style={{ padding: '36px 40px' }}>
        {/* ── Subcategory Pills ── */}
        {subcategories.length > 0 && (
          <div style={{ marginBottom: 36 }}>
            <p style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 16 }}>Filter by Type</p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button
                onClick={() => setActiveSubcat('all')}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 18px', borderRadius: 8, cursor: 'pointer',
                  background: activeSubcat === 'all' ? 'rgba(212,168,83,0.12)' : 'var(--surface)',
                  border: `1px solid ${activeSubcat === 'all' ? 'var(--border-2)' : 'var(--border)'}`,
                  color: activeSubcat === 'all' ? 'var(--gold)' : 'var(--text-2)',
                  fontSize: 12, fontWeight: 500, transition: 'all 0.25s',
                }}
              >
                <span style={{ fontSize: 20 }}>🍽</span>
                <span>All</span>
              </button>
              {subcategories.map(sub => (
                <button
                  key={sub.id}
                  onClick={() => setActiveSubcat(sub.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
                    background: activeSubcat === sub.id ? 'rgba(212,168,83,0.12)' : 'var(--surface)',
                    border: `1px solid ${activeSubcat === sub.id ? 'var(--border-2)' : 'var(--border)'}`,
                    color: activeSubcat === sub.id ? 'var(--gold)' : 'var(--text-2)',
                    fontSize: 12, transition: 'all 0.25s',
                  }}
                  onMouseEnter={e => { if (activeSubcat !== sub.id) { e.currentTarget.style.borderColor = 'var(--border-2)'; e.currentTarget.style.color = 'var(--text)'; }}}
                  onMouseLeave={e => { if (activeSubcat !== sub.id) { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-2)'; }}}
                >
                  {sub.imageBlob && (
                    <img src={sub.imageBlob} alt={sub.name} style={{ width: 30, height: 30, borderRadius: 6, objectFit: 'cover' }} />
                  )}
                  <span>{sub.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Vendors Grid ── */}
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <p style={{ fontSize: 13, color: 'var(--text-3)' }}>
            {filteredVendors.length} result{filteredVendors.length !== 1 ? 's' : ''}
          </p>
        </div>

        {filteredVendors.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <div style={{ fontSize: 52, marginBottom: 16 }}>🏪</div>
            <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: 24, marginBottom: 10 }}>No vendors found</h3>
            <p style={{ color: 'var(--text-3)' }}>Try a different filter or check back later.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 20 }}>
            {filteredVendors.map((vendor, i) => {
              const open = isOpen(vendor.openTime);
              return (
                <Link
                  key={vendor.id}
                  to={`/vendor/${vendor.id}`}
                  style={{ textDecoration: 'none', animationDelay: `${i * 0.06}s` }}
                  className="anim-up"
                >
                  <div
                    style={{
                      background: 'var(--surface)', border: '1px solid var(--border)',
                      borderRadius: 10, overflow: 'hidden', cursor: 'pointer',
                      transition: 'all 0.35s var(--ease)',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.borderColor = 'var(--border-2)'; e.currentTarget.style.boxShadow = '0 18px 50px rgba(0,0,0,0.4)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    {/* Image */}
                    <div style={{ position: 'relative', paddingTop: '55%', overflow: 'hidden', background: 'var(--surface-2)' }}>
                      {vendor.imageBlob ? (
                        <img
                          src={vendor.imageBlob}
                          alt={vendor.name}
                          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s' }}
                        />
                      ) : (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 52 }}>🏪</div>
                      )}
                      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top,rgba(9,12,10,0.6) 0%,transparent 50%)' }} />

                      {/* Badges */}
                      <div style={{ position: 'absolute', top: 12, left: 12, display: 'flex', gap: 6 }}>
                        <span style={{
                          padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 500,
                          background: open ? 'rgba(76,175,125,0.85)' : 'rgba(212,83,83,0.85)',
                          color: 'white', backdropFilter: 'blur(6px)',
                        }}>
                          {open ? '● Open' : '● Closed'}
                        </span>
                        {vendor.recommended && (
                          <span className="badge badge-gold" style={{ backdropFilter: 'blur(6px)', fontSize: 9 }}>★ Recommended</span>
                        )}
                      </div>

                      {/* Veg/NonVeg */}
                      <div style={{ position: 'absolute', top: 12, right: 12 }}>
                        <span style={{
                          width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: 'rgba(9,12,10,0.8)', borderRadius: 4, fontSize: 14, backdropFilter: 'blur(6px)',
                        }}>
                          {vendor.restaurantType === 'Veg' ? '🟢' : vendor.restaurantType === 'Non-Veg' ? '🔴' : '🟡'}
                        </span>
                      </div>
                    </div>

                    {/* Info */}
                    <div style={{ padding: '16px 18px 18px' }}>
                      <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: 17, fontWeight: 400, marginBottom: 5, lineHeight: 1.25 }}>
                        {vendor.name}
                      </h3>
                      {vendor.legalName && vendor.legalName !== vendor.name && (
                        <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 8 }}>{vendor.legalName}</div>
                      )}

                      {/* Meta row */}
                      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 12 }}>
                        {vendor.area?.area && (
                          <span style={{ fontSize: 11, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 4 }}>
                            📍 {vendor.area.area}
                          </span>
                        )}
                        {vendor.deliveryCharge === 0 ? (
                          <span style={{ fontSize: 11, color: 'var(--emerald-3)', fontWeight: 500 }}>Free Delivery</span>
                        ) : vendor.deliveryCharge > 0 ? (
                          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>₹{vendor.deliveryCharge} delivery</span>
                        ) : null}
                        {vendor.restaurantType && (
                          <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{vendor.restaurantType}</span>
                        )}
                      </div>

                      {/* Offer */}
                      {vendor.offerEnabled && vendor.offer && (
                        <div style={{ background: 'rgba(212,168,83,0.08)', border: '1px solid var(--border)', borderRadius: 6, padding: '7px 12px', fontSize: 11, color: 'var(--gold)', marginBottom: 12 }}>
                          🎁 {vendor.offer}
                        </div>
                      )}

                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                          {vendor.paymentType === 'Free' ? '🆓 Free listing' : `💳 ${vendor.paymentType}`}
                        </span>
                        <span style={{ fontSize: 11, color: 'var(--gold)', fontWeight: 500 }}>View Menu →</span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
